// Thin wrapper around fetch for talking to the FastAPI backend.
// Sessions are a Bearer JWT (see backend/app/auth.py) stored in localStorage —
// there's no cookie involved, so every authenticated call attaches it manually.

const API_BASE = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;
const SESSION_KEY = "mk_session"; // { session_token, email, name }

export function getSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

/**
 * @param {string} path - e.g. "/auth/login"
 * @param {object} opts
 * @param {"GET"|"POST"} [opts.method]
 * @param {object} [opts.body]
 * @param {boolean} [opts.auth] - attach the logged-in user's session token
 * @param {string} [opts.adminKey] - attach this as the admin bearer token instead
 * @param {boolean} [opts.raw] - return the raw Response instead of parsed JSON (for PDF downloads etc.)
 */
export async function apiFetch(path, opts = {}) {
  const { method = "GET", body, auth = false, adminKey, raw = false } = opts;

  const headers = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";

  if (adminKey) {
    headers["Authorization"] = `Bearer ${adminKey}`;
  } else if (auth) {
    const session = getSession();
    if (session?.session_token) {
      headers["Authorization"] = `Bearer ${session.session_token}`;
    }
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (raw) return res;

  let data = null;
  try {
    data = await res.json();
  } catch {
    // no JSON body (e.g. some error pages) — fine, data stays null
  }

  if (!res.ok) {
    const message = (data && data.detail) || `Request failed (${res.status})`;
    const err = new Error(typeof message === "string" ? message : JSON.stringify(message));
    err.status = res.status;
    throw err;
  }

  return data;
}

export { API_BASE };
